// Публикация чата в свой git и на сайт (этап 26.4): представление состояния из
// GET /api/chats/{id}/publication. Данные и правила — на backend, здесь только вид.
import { TEXT } from './ui-text.js';

// «Этап 2. Выбор плитки» — как заголовок страницы на сайте.
export function stageLabel(stage, number) {
  const base = TEXT.publish.pageStage.replace('{number}', String(number));
  return stage?.title ? `${base}. ${stage.title}` : base;
}

// Значок под названием чата: { key, label, tone } или null (не опубликован).
// tone: ok | busy | git | danger. «Правка из git» держится, пока владелец не открыл шторку.
export function publicationBadge(publication, { syncing = false, importedFresh = false } = {}) {
  if (!publication?.published) {
    return syncing ? { key: 'syncing', label: TEXT.publish.badge.syncing, tone: 'busy' } : null;
  }
  if (syncing || publication.status === 'pending') {
    return { key: 'syncing', label: TEXT.publish.badge.syncing, tone: 'busy' };
  }
  if (publication.status === 'conflict') {
    return { key: 'conflict', label: TEXT.publish.badge.conflict, tone: 'danger' };
  }
  if (publication.status === 'error') {
    return { key: 'error', label: TEXT.publish.badge.error, tone: 'danger' };
  }
  if (importedFresh) {
    return { key: 'imported', label: TEXT.publish.badge.imported, tone: 'git' };
  }
  return { key: 'synced', label: TEXT.publish.badge.synced, tone: 'ok' };
}

export function formatTime(iso) {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n) => String(n).padStart(2, '0');
  const today = new Date();
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return date.toDateString() === today.toDateString() ? time : `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${time}`;
}

// Дерево будущего раздела для шторки «Опубликовать»: обзор, готовые этапы, расшифровка.
export function previewTree(stages, { hasState = false, includeTranscript = true } = {}) {
  const items = [{ kind: 'state', label: hasState ? TEXT.publish.treeOverview : TEXT.publish.treeOverviewEmpty }];
  (stages || []).forEach((stage, index) => {
    if (stage.status === 'ready') {
      items.push({ kind: 'stage', label: stageLabel(stage, index + 1) });
    }
  });
  if (includeTranscript) {
    items.push({ kind: 'transcript', label: TEXT.publish.treeTranscript });
  }
  return items;
}

export function readyStageCount(stages) {
  return (stages || []).filter((stage) => stage.status === 'ready').length;
}

// { stageId: importedAt } — отметка «изменено в git» на карточке этапа.
export function importedByStage(publication) {
  const result = {};
  for (const file of publication?.files || []) {
    if (file.kind === 'stage' && file.stageId && file.importedAt) {
      result[file.stageId] = file.importedAt;
    }
  }
  return result;
}

export function stateImportedAt(publication) {
  return (publication?.files || []).find((file) => file.kind === 'state')?.importedAt || null;
}

export function conflictFiles(publication) {
  return (publication?.files || []).filter((file) => file.state === 'conflict' || file.state === 'unparsed');
}

// Подпись страницы раздела: «Обзор», «Этап 2», «Расшифровка».
export function pageLabel(file, stages) {
  if (file.kind === 'state') {
    return TEXT.publish.pageState;
  }
  if (file.kind === 'transcript') {
    return TEXT.publish.pageTranscript;
  }
  const index = (stages || []).findIndex((stage) => stage.stageId === file.stageId);
  if (index >= 0) {
    return stageLabel(stages[index], index + 1);
  }
  return TEXT.publish.pageStage.replace('{number}', '?');
}

// Тост о правке из git по событию publication.imported.
export function importedMessage(item, stages) {
  if (item?.kind === 'state') {
    return TEXT.publish.importedState;
  }
  if (item?.kind === 'article') {
    return TEXT.publish.importedArticle;
  }
  const index = (stages || []).findIndex((stage) => stage.stageId === item?.stageId);
  return TEXT.publish.importedStage.replace('{number}', index >= 0 ? String(index + 1) : '');
}

export function statusLine(publication) {
  if (!publication?.published) {
    return '';
  }
  const status = TEXT.publish.status[publication.status] || '';
  return status
    .replace('{time}', formatTime(publication.publishedAt))
    .replace('{error}', publication.error || '');
}

// Врезки и вкладки MkDocs Material в просмотре статьи (этап 26.5): рендер приложения их
// не знает, поэтому «!!! note "Заголовок"» становится цитатой с жирным заголовком, а
// «=== "Вкладка"» — жирной подписью; сдвинутый на 4 пробела текст — их содержимое.
const ADMONITION = /^(?:!!!|\?\?\?\+?)\s+([\w-]+)(?:\s+"([^"]*)")?\s*$/;
const TAB = /^===\+?\s+"([^"]*)"\s*$/;
const INDENT = /^( {4}|\t)/;

export function materialPreview(markdown) {
  const out = [];
  let mode = null;
  for (const line of String(markdown || '').split('\n')) {
    const admonition = line.match(ADMONITION);
    if (admonition) {
      mode = 'admonition';
      out.push(`> **${admonition[2] || TEXT.publish.admonitions[admonition[1]] || admonition[1]}**`, '>');
      continue;
    }
    const tab = line.match(TAB);
    if (tab) {
      mode = 'tab';
      out.push(`**${tab[1]}**`, '');
      continue;
    }
    if (mode && (INDENT.test(line) || line.trim() === '')) {
      const content = line.replace(INDENT, '');
      out.push(mode === 'admonition' ? (content ? `> ${content}` : '>') : content);
      continue;
    }
    mode = null;
    out.push(line);
  }
  return out.join('\n');
}
